import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <img 
            src="/nightbuzz-logo.png" 
            alt="NightBuZz" 
            className="h-10 cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>
          Privacy Policy
        </h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-slate-300">
          <p className="text-sm text-slate-400">Last Updated: March 2025</p>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">1. Introduction</h2>
            <p>
              NightBuZz ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-white mt-6 mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name and username</li>
              <li>Email address</li>
              <li>Age and gender (for demographic features)</li>
              <li>Profile picture</li>
              <li>Payment information (processed securely via Stripe)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Location Data</h3>
            <p>
              With your permission, we collect your location to show nearby bars and enable check-ins. 
              You can disable location services at any time through your device settings.
            </p>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Check-in history</li>
              <li>Bars visited and searched</li>
              <li>Videos and content uploaded</li>
              <li>Interactions with other users</li>
              <li>Device information and IP address</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and maintain the Service</li>
              <li>Show you nearby bars and personalized recommendations</li>
              <li>Enable social features (friend tagging, check-ins)</li>
              <li>Generate anonymous crowd statistics for bar listings</li>
              <li>Process payments for premium features</li>
              <li>Send you updates and promotional communications (with consent)</li>
              <li>Improve our Service and develop new features</li>
              <li>Ensure safety and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">4. Information Sharing</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Other Users:</strong> Your profile, check-ins, and videos are visible to other users</li>
              <li><strong>Bar Owners:</strong> Anonymous demographic data about their venues</li>
              <li><strong>Service Providers:</strong> Third parties who help us operate (Stripe, Yelp, hosting)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="mt-4 font-semibold text-white">
              We do NOT sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encrypted data transmission (HTTPS/SSL)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Limited employee access to personal data</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee 
              absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correct:</strong> Update inaccurate information</li>
              <li><strong>Delete:</strong> Request deletion of your account and data</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li>
              <li><strong>Restrict:</strong> Limit how we use your data</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@getnightbuzz.com" className="text-purple-400 hover:text-purple-300">
                privacy@getnightbuzz.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">7. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage, 
              and deliver personalized content. You can control cookies through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">8. Third-Party Services</h2>
            <p>Our Service integrates with third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Yelp:</strong> For bar information and reviews</li>
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>Social Media:</strong> For sharing features</li>
            </ul>
            <p className="mt-4">
              These services have their own privacy policies that govern their use of your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">9. Children's Privacy</h2>
            <p>
              NightBuZz is intended for users 21 years of age and older. We do not knowingly collect 
              information from anyone under 21. If we learn we have collected information from someone 
              under 21, we will delete it immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">10. Data Retention</h2>
            <p>
              We retain your personal data for as long as your account is active or as needed to provide 
              services. We may retain certain information as required by law or for legitimate business 
              purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">11. California Privacy Rights (CCPA)</h2>
            <p>
              California residents have additional rights under the CCPA, including the right to know 
              what personal information is collected and the right to request deletion. Contact us to 
              exercise these rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes 
              by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">13. Contact Us</h2>
            <p>
              For questions about this Privacy Policy, contact us at: <br />
              <a href="mailto:privacy@getnightbuzz.com" className="text-purple-400 hover:text-purple-300">
                privacy@getnightbuzz.com
              </a>
            </p>
            <p className="mt-4">
              NightBuZz<br />
              <a href="https://getnightbuzz.com" className="text-purple-400 hover:text-purple-300">
                getnightbuzz.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
